"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DEFAULT_EMAIL_SOURCE } from "@/lib/constants/email-capture";
import { useEffect, useMemo, useState } from "react";

const PROMO_IMAGE_SRC = "/images/marketing/home/teacher/teacher-sitting-art.webp";

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

type EmailCaptureApiResponse = { ok?: boolean; error?: string };

async function readJsonSafely(res: Response): Promise<EmailCaptureApiResponse | null> {
  try {
    const json = (await res.json()) as unknown;
    if (!json || typeof json !== "object") return null;
    const obj = json as Record<string, unknown>;
    return {
      ok: typeof obj.ok === "boolean" ? obj.ok : undefined,
      error: typeof obj.error === "string" ? obj.error : undefined,
    };
  } catch {
    return null;
  }
}

type SubmitResult = { ok: true } | { ok: false; message: string };

export interface EmailCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
  onSuccess?: () => void;
}

export function EmailCaptureModal({
  open,
  onOpenChange,
  source = DEFAULT_EMAIL_SOURCE,
  onSuccess,
}: EmailCaptureModalProps) {
  const [email, setEmail] = useState("");
  const [wantsUpdates, setWantsUpdates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preload the promo image so the modal doesn't reflow mid-animation when it first opens.
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const img = new Image();
      img.src = PROMO_IMAGE_SRC;
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("EmailCaptureModal: promo image preload failed", err);
      }
      // Non-critical: best-effort only.
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setWantsUpdates(false);
      setIsSubmitting(false);
      setError(null);
    }
  }, [open]);

  const canSubmit = useMemo(() => !isSubmitting, [isSubmitting]);

  const handleEmailCaptureSuccess = () => {
    onOpenChange(false);
    onSuccess?.();
  };

  async function submitEmailCaptureRequest(input: {
    email: string;
    wantsUpdates: boolean;
    source: string;
  }): Promise<SubmitResult> {
    let res: Response;
    try {
      res = await fetch("/api/email-capture", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
    } catch {
      return { ok: false, message: "Connection error. Please check your network and try again." };
    }

    const data = await readJsonSafely(res);

    // Duplicate submissions are treated as success so the user can proceed.
    if (res.status === 409) return { ok: true };

    if (res.ok && data?.ok) return { ok: true };

    if (res.status >= 500) {
      return { ok: false, message: "Something went wrong. Please try again later." };
    }

    // 4xx: show server-provided error if present (e.g. validation, rate limit)
    return { ok: false, message: data?.error || "Failed to save email" };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;
    setError(null);

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      // Email capture is optional; allow the user to continue without submitting.
      handleEmailCaptureSuccess();
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitEmailCaptureRequest({
        email: trimmedEmail,
        wantsUpdates,
        source,
      });

      if (result.ok) {
        handleEmailCaptureSuccess();
        return;
      }

      setError(result.message);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg px-4 overflow-hidden sm:!rounded-3xl">
        <DialogTitle className="sr-only">Free for Educators - PlayMath.org</DialogTitle>
        {/* Placeholder image area */}
        <div className="flex items-center justify-center pt-6">
          <img
            src={PROMO_IMAGE_SRC}
            alt="Educator Promo"
            width={240}
            height={240}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="w-60 h-60 object-contain"
          />
        </div>

        <div className="px-6 pb-6 pt-4">
          <h2 className="text-3xl md:text-4xl text-center mb-3">Free for Educators!</h2>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label htmlFor="educator-email" className="block text-sm font-medium mb-1">
                Educator Email (Optional)
              </label>
              <Input
                type="email"
                id="educator-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.edu"
              />
            </div>

            <p className="text-md text-black text-left mb-2">
              Great tools start with great teachers. Join us as we craft something truly useful for
              the classroom.
            </p>

            <p className="text-xs text-gray-600">
              By using PlayMath.org, you agree to our{" "}
              <a
                className="text-brand-purple underline"
                href="/terms"
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                className="text-brand-purple underline"
                href="/privacy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
              .
            </p>

            <label htmlFor="wants-updates" className="flex items-center gap-2 text-sm">
              <Checkbox
                id="wants-updates"
                checked={wantsUpdates}
                onCheckedChange={(v) => setWantsUpdates(Boolean(v))}
              />
              <span>Sign me up for updates about new games and class tools!</span>
            </label>

            {error && (
              <div className="text-red-600 text-sm" role="alert">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-brand-purple hover:bg-brand-purple-dark rounded-full"
              disabled={!canSubmit}
            >
              {isSubmitting ? "Saving..." : "Get Free Access"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
